"use client";

// Importación de componentes necesarios
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components";
import { buttonVariants } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { authenticateUser } from "@/features/auth/login/login.action";
import {
	type LoginInput,
	LoginSchema,
} from "@/features/auth/schema/auth.schema";
import { LoadingButton } from "@/features/ui/button/loading-button";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
// import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { EyeOpenIcon, EyeClosedIcon } from "@radix-ui/react-icons";
import { useRouter } from "next/navigation";
import { useUser } from "@/contexts/user-provider";
import { Paths } from "@/features/core/navigation/constants";
import { GoogleIcon } from "@/components/ui/icons/google-icon";
import { toast } from "@/hooks/use-toast";
import type { UserModel } from "@/domain/models/user.model";

type LoginMessage = { success: boolean; message: string };

// Componente de página de inicio de sesión
export default function LoginPage() {
	// Configuración del formulario utilizando react-hook-form y zod para validación
	const form = useForm<LoginInput>({
		mode: "onChange",
		resolver: zodResolver(LoginSchema),
		defaultValues: {
			username: "",
			password: "",
		},
	});

	// Estado para mostrar mensajes de error de inicio de sesión
	const [loginMessage, setLoginMessage] = useState<LoginMessage | null>(null);
	// Estado para mostrar si el formulario está cargando
	const [isLoadingForm, setIsLoadingForm] = useState(false);
	// Referencia para establecer foco en el campo de usuario
	const usernameRef = useRef<HTMLInputElement>(null); // Define el tipo HTMLInputElement para la referencia

	// Estado para controlar la visibilidad temporal de la contraseña
	const [showPassword, setShowPassword] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	// const [mounted, setMounted] = useState(false);

	// Función para alternar la visibilidad de la contraseña
	const handleTogglePassword = () => {
		// Si la contraseña está visible, ocúltala y cancela el temporizador
		if (showPassword) {
			setShowPassword(false);
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		} else {
			// Si está oculta, revela la contraseña y configura el temporizador
			setShowPassword(true);
			timeoutRef.current = setTimeout(() => {
				setShowPassword(false); // Oculta la contraseña automáticamente después de 5 segundos
			}, 5000);
		}
	};

	useEffect(() => {
		// Establece el foco en el campo de usuario al montar el componente
		if (usernameRef.current) {
			usernameRef.current.focus();
		}
	}, []);

	// Usamos useRouter para redirección
	const router = useRouter();

	// // Se ejecuta solo después de que el componente esté montado en el cliente
	// useEffect(() => {
	// 	setMounted(true);
	// }, []);

	// Redirección después de un login exitoso
	useEffect(() => {
		if (loginMessage) {
			// console.log(loginMessage);
			if (loginMessage?.success ?? false) {
				router.replace(Paths.Projects); // Redirige a la página de proyectos
			}
		}
	}, [loginMessage, router]);

	// Usamos useUserContext para acceder a setUser
	const { setUser } = useUser();

	// Función que maneja el envío del formulario
	async function handleSubmit(data: LoginInput) {
		if (isLoadingForm) return; // Prevenir la ejecución si el formulario ya está en carga

		// Indica que el formulario está en estado de carga
		setIsLoadingForm(true);

		try {
			// Llama a la función de autenticación con los datos del formulario
			const loginResponse = await authenticateUser(data);

			// Guardamos el usuario en el contexto si la autenticación es exitosa
			if (loginResponse.success && loginResponse.user) {
				setUser(loginResponse.user); // Actualiza el contexto con el usuario autenticado
			}

			setLoginMessage({
				success: loginResponse.success,
				message: loginResponse.message,
			});
		} catch (error) {
			// Manejo de errores inesperados durante la autenticación
			const errorMessage =
				error instanceof Error ? error.message : "Unexpected error occurred";

			// console.log(errorMessage);

			setLoginMessage({
				success: false,
				message: "Error interno del servidor. Intente nuevamente más tarde.",
			});
		} finally {
			// Finaliza el estado de carga del formulario
			setIsLoadingForm(false);
		}
	}

	// Escuchar el 'postMessage' desde la ventana emergente
	// Efecto que escucha los mensajes de la ventana emergente
	useEffect(() => {
		// Solo ejecutar en cliente
		if (typeof window === "undefined") return;

		function handleMessage(event: MessageEvent) {
			// console.log("Mensaje recibido del popup:", event.data);

			// Asegurar que el mensaje proviene de nuestra aplicación
			const expectedOrigin = window.location.origin;
			if (event.origin !== expectedOrigin) {
				console.warn("Mensaje ignorado de origen desconocido:", event.origin);
				return;
			}

			if (typeof event.data === "object" && event.data !== null) {
				if (event.data.type === "google-auth-success") {
					console.log("Autenticación con Google exitosa, redirigiendo...");

					if (event.data.user) {
						const userLogin = event.data.user as UserModel;
						console.log(event.data);
						console.log(userLogin);
						setUser(userLogin); // <-- ACTUALIZA EL CONTEXTO
					}

					router.push("/projects");
				} else if (event.data.type === "google-auth-error") {
					let errorMessage = "Error de autenticación.";

					if (event.data.error === "UserNotFound") {
						errorMessage = "No tienes acceso. Contacta al administrador.";
					} else if (event.data.error === "InvalidToken") {
						errorMessage = "Error al verificar credenciales.";
					} else if (event.data.error === "NoEmail") {
						errorMessage = "No pudimos obtener tu correo.";
					}

					toast({
						title: "Error de autenticación",
						description: errorMessage,
						variant: "destructive",
					});
				}
			}
		}

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [router, setUser]);

	// Abre el popup
	const handleGoogleLogin = () => {
		if (isLoadingForm || typeof window === "undefined") return;

		const width = 600;
		const height = 600;
		const left = window.innerWidth / 2 - width / 2;
		const top = window.innerHeight / 2 - height / 2;

		window.open(
			"/api/auth/google",
			"Login con Google",
			`popup=yes,width=${width},height=${height},top=${top},left=${left}`,
		);
	};

	// Estructura de la interfaz de usuario de la página
	return (
		<div className="flex h-screen w-full items-center justify-center px-4">
			<Card className="mx-auto w-full max-w-sm border-none">
				<CardHeader>
					<CardTitle className="text-2xl">Gestión de Proyectos</CardTitle>
					<CardDescription>Inicia sesión con tu cuenta</CardDescription>
				</CardHeader>
				<CardContent>
					<Form {...form}>
						{/* {loginMessage != null && loginMessage.message != "" && (
							<div className="text-red-500 text-sm">{loginMessage.message}</div>
						)} */}
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="flex flex-col gap-2"
							method="POST"
						>
							<FormField
								control={form.control}
								name="username"
								render={({ field }) => (
									<FormItem className="w-full">
										<FormLabel>Cuenta de usuario</FormLabel>
										<FormControl>
											<Input
												placeholder="Usuario o correo electrónico"
												{...field}
												ref={usernameRef}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem className="w-full">
										<FormLabel>Contraseña</FormLabel>
										<FormControl>
											{/* Contenedor para el input y el botón de ícono */}
											<div className="relative w-full">
												<Input
													type={showPassword ? "text" : "password"}
													placeholder="Contraseña"
													autoComplete="new-password"
													{...field}
												/>
												{/* Ícono de ojo para mostrar/ocultar contraseña */}
												<button
													type="button"
													className="-translate-y-1/2 absolute top-1/2 right-2 transform text-gray-500"
													onClick={handleTogglePassword}
													aria-label="Mostrar contraseña temporalmente"
												>
													{showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
												</button>
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<LoadingButton loading={isLoadingForm}>Continuar</LoadingButton>

							{/* Línea gris debajo del botón Continuar */}
							<div className="relative my-4">
								<div className="absolute inset-0 flex items-center">
									<div className="w-full border-gray-300 border-t" />
								</div>
							</div>

							{/* <Link
								href={isLoadingForm ? "#" : "/api/auth/google"}
								className={cn(
									buttonVariants({ variant: "outline" }),
									"w-full",
									{
										disabled: isLoadingForm,
										"pointer-events-none": isLoadingForm,  // Desactiva los clics
									}
								)}>
								<GoogleIcon className="mr-2 h-5 w-5" />
								Continuar con Google
							</Link> */}

							<button
								type="button"
								onClick={handleGoogleLogin}
								disabled={isLoadingForm}
								className={cn(
									buttonVariants({ variant: "outline" }),
									"w-full",
									{
										disabled: isLoadingForm,
										"pointer-events-none": isLoadingForm,
									},
								)}
							>
								<GoogleIcon className="mr-2 h-5 w-5" />
								Continuar con Google
							</button>

							{/* Mensaje debajo del botón de Google */}
							<p className="mt-4 text-justify text-gray-600 text-xs">
								Usa un correo electrónico asociado a tu organización para
								colaborar con compañeros de equipo con mayor facilidad
							</p>
						</form>
					</Form>
				</CardContent>
			</Card>
		</div>
	);
}
